import i18n, { portalLanguage } from '../i18n';

/**
 * Service and plan names in the language on screen (QA 2026-09-25 #6).
 *
 * Names come from the database, in English, and the database is not
 * changed. In French or Spanish a name is looked up, in order:
 *   1. by the service's `nameKey` (`services.byKey.<nameKey>`), the stable
 *      id the catalogue rows carry for plans, oil changes, fees and so on;
 *   2. by its English name (`services.byName.<slug>`), for the add-ons the
 *      catalogue has no nameKey for ("Cabin Air Filter Replacement");
 *   3. otherwise the database name is shown as it is - and `isTranslated`
 *      says so, so the caller can mark it `lang="en"` for screen readers.
 * English always shows the database name, exactly as before.
 */

type Named = {
  name?: string | null;
  nameKey?: string | null;
};

/** "Cabin Air Filter Replacement" -> "cabin_air_filter_replacement". */
export function serviceNameSlug(name?: string | null): string {
  return String(name || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');
}

function lookup(service: Named): string | null {
  if (service.nameKey) {
    const key = `services.byKey.${service.nameKey}`;
    if (i18n.exists(key)) return i18n.t(key);
  }
  const slug = serviceNameSlug(service.name);
  if (slug) {
    const key = `services.byName.${slug}`;
    if (i18n.exists(key)) return i18n.t(key);
  }
  return null;
}

/** The name to show for a catalogue row, and whether it is translated. */
export function serviceNameText(service: Named): {
  text: string;
  isTranslated: boolean;
} {
  const fallback = service.name || '';
  if (portalLanguage() === 'en') return { text: fallback, isTranslated: true };
  const found = lookup(service);
  return found
    ? { text: found, isTranslated: true }
    : { text: fallback, isTranslated: false };
}

/** Just the name (see serviceNameText). */
export function serviceDisplayName(service: Named): string {
  return serviceNameText(service).text;
}

/**
 * `lang` for an element holding database text that stayed English on a
 * French or Spanish page, so a screen reader reads it with an English
 * voice. Undefined whenever the text is in the page's own language.
 */
export function untranslatedLang(isTranslated: boolean): 'en' | undefined {
  return !isTranslated && portalLanguage() !== 'en' ? 'en' : undefined;
}
